import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Form, Formik } from 'formik';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/Checkbox';
import { CircularProgress } from 'src/components/CircularProgress';
import { RichTextEditor } from 'src/components/RichTextEditor';
import { Stack } from 'src/components/Stack';
import { StickyCard } from 'src/components/StickyCard';
import { Page } from 'src/models/page/_types';
import { fetchPage, previewPage, updatePage } from 'src/models/page/page';
import { updatePlatform } from 'src/models/platform/platform';
import { FormCheckbox } from 'src/shared/Forms/Fields/FormCheckbox';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';
import * as Yup from 'yup';

interface PageEditorProps {
  platform: any;
  cachedSlug: string;
  pageTitle: string;
}

// Validation schema that conditionally requires content or URL
const validationSchema = Yup.object().shape({
  content: Yup.string().when('useExternalUrl', {
    is: (value: boolean) => !value,
    then: (schema) => schema.required('Page content is required'),
    otherwise: (schema) => schema,
  }),
  useExternalUrl: Yup.boolean(),
  externalUrl: Yup.string().when('useExternalUrl', {
    is: (value: boolean) => !!value,
    then: (schema) =>
      schema.url('Please enter a valid URL').required('URL is required'),
    otherwise: (schema) => schema,
  }),
});

export const PageEditor: React.FC<PageEditorProps> = ({
  platform,
  cachedSlug,
  pageTitle,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'pageEditor' });
  const queryClient = useQueryClient();
  const [showRawHtml, setShowRawHtml] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const previewIframeRef = useRef<HTMLIFrameElement>(null);

  // Format HTML for better readability when editing raw HTML
  const formatHtml = (html: string) => {
    // Simple formatting - more sophisticated formatting could be added
    return html.replace(/></g, '>\n<');
  };

  // Mutation to update the page content
  const pageMutation = useMutation(
    async (values: Partial<Page>) => {
      if (!values.id) return null;

      return updatePage(values.id, {
        ...values,
      });
    },
    {
      onSuccess: () => {
        toast.success(t('pageUpdateSuccess'));
        queryClient.invalidateQueries(['page', cachedSlug]);
      },
      onError: (error) => {
        toast.error(api.formatError(error, t('pageUpdateError')));
      },
    }
  );

  // Mutation to update the platform URL
  const platformUrlMutation = useMutation(
    async ({
      termsOfServiceUrl,
      privacyPolicyUrl,
    }: {
      termsOfServiceUrl?: string;
      privacyPolicyUrl?: string;
    }) => {
      if (cachedSlug === 'terms-of-service') {
        return updatePlatform(platform.id, {
          termsOfServiceUrl: termsOfServiceUrl,
        });
      } else {
        return updatePlatform(platform.id, {
          privacyPolicyUrl: privacyPolicyUrl,
        });
      }
    },
    {
      onSuccess: () => {
        toast.success(t('urlUpdateSuccess', { pageTitle }));
        queryClient.invalidateQueries(['platform', platform.id]);
      },
      onError: (error) => {
        toast.error(api.formatError(error, t('urlUpdateError', { pageTitle })));
      },
    }
  );

  // Update the preview when content changes
  const updatePreview = useCallback(
    async (htmlContent: string) => {
      try {
        const html = await previewPage(cachedSlug, htmlContent, platform?.id);
        setPreviewHtml(html);
      } catch (error) {
        toast.error(api.formatError(error, t('previewError')));
      }
    },
    [platform?.id, cachedSlug, t]
  );

  // Query to fetch the page
  const { data: page, isLoading } = useQuery<Page>({
    queryKey: ['page', cachedSlug],
    queryFn: async () => {
      try {
        const fetchedPage = await fetchPage(cachedSlug);
        // Reset raw HTML mode and update preview when fetching new data
        setShowRawHtml(false);
        if (fetchedPage.content) {
          updatePreview(fetchedPage.content);
        }
        return fetchedPage;
      } catch (error) {
        toast.error(api.formatError(error, t('pageLoadError')));
        throw error; // Re-throw the error so react-query handles it properly
      }
    },
    // Force refetch on mount to ensure we have the latest data
    refetchOnMount: true,
  });

  // Get the URL from the platform based on the cachedSlug
  const externalUrl =
    cachedSlug === 'terms-of-service'
      ? platform?.termsOfServiceUrl || ''
      : platform?.privacyPolicyUrl || '';

  if (isLoading) {
    return <CircularProgress size="medium" />;
  }

  const initialValues = {
    id: page?.id,
    name: page?.name || pageTitle,
    cachedSlug: cachedSlug,
    content: page?.content || '',
    useExternalUrl: !!externalUrl,
    externalUrl: externalUrl,
  };

  const handleSubmit = (
    values: typeof initialValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    if (values.useExternalUrl) {
      // Update the platform URL
      if (cachedSlug === 'terms-of-service') {
        platformUrlMutation.mutate({ termsOfServiceUrl: values.externalUrl });
      } else {
        platformUrlMutation.mutate({ privacyPolicyUrl: values.externalUrl });
      }
    } else {
      // Update the page content
      pageMutation.mutate({
        id: values.id,
        name: values.name,
        cachedSlug: values.cachedSlug,
        content: values.content,
      });

      // If there was a URL before, clear it
      if (externalUrl) {
        const urlField =
          cachedSlug === 'terms-of-service'
            ? 'termsOfServiceUrl'
            : 'privacyPolicyUrl';

        const params = {};
        params[urlField] = '';
        platformUrlMutation.mutate(params);
      }
    }
    setSubmitting(false);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Add key to entire Formik component to force remount when content changes */}
      <Formik
        key={`form-${page?.id}-${page?.content?.substring(
          0,
          20
        )}-${externalUrl}`}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          handleSubmit,
          setFieldValue,
          isSubmitting,
          submitForm,
        }) => (
          <Form onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <FormCheckbox
                useSwitch
                name="useExternalUrl"
                label={t('useExternalUrl')}
                onChange={(e) => {
                  // When toggling from URL to content mode, clear the URL immediately
                  if (!e.target.checked) {
                    const urlField =
                      cachedSlug === 'terms-of-service'
                        ? 'termsOfServiceUrl'
                        : 'privacyPolicyUrl';

                    const params = {};
                    params[urlField] = '';

                    platformUrlMutation.mutate(params);
                  }
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                {t('useExternalUrlDescription')}
              </Typography>
            </Box>

            {values.useExternalUrl ? (
              <Box sx={{ mb: 3 }}>
                <FormTextField
                  label={t('externalUrl')}
                  fullWidth
                  name="externalUrl"
                  placeholder={`e.g. https://${platform?.host}/terms-of-service`}
                  helperText={t('externalUrlHelperText')}
                />

                <Box
                  sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    variant="contained"
                    type="button"
                    disabled={isSubmitting || platformUrlMutation.isLoading}
                    onClick={submitForm}
                  >
                    {isSubmitting || platformUrlMutation.isLoading
                      ? t('saving')
                      : t('saveUrl')}
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                <Box sx={{ mb: 3 }}>
                  <FormTextField label={t('pageTitle')} fullWidth name="name" />
                </Box>
                <Grid container spacing={2}>
                  {/* Editor Section */}
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <StickyCard
                      title={values.name}
                      actions={
                        <Button
                          variant="contained"
                          type="button"
                          disabled={isSubmitting || pageMutation.isLoading}
                          onClick={submitForm}
                        >
                          {isSubmitting || pageMutation.isLoading
                            ? t('saving')
                            : t('saveChanges')}
                        </Button>
                      }
                    >
                      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                        <Checkbox
                          label={t('editHtmlDirectly')}
                          checked={showRawHtml}
                          onChange={(e) => {
                            // Format HTML when switching to HTML mode
                            if (e.target.checked && values.content) {
                              const formattedHtml = formatHtml(values.content);
                              setFieldValue('content', formattedHtml);
                            }
                            setShowRawHtml(e.target.checked);
                          }}
                        />
                      </Stack>
                      {showRawHtml ? (
                        <FormTextField
                          multiline
                          rows={20}
                          fullWidth
                          name="content"
                          placeholder={t('editHtmlPlaceholder')}
                          onChange={(e) => {
                            updatePreview(e.target.value);
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              whiteSpace: 'pre !important',
                              overflowX: 'auto !important',
                              wordWrap: 'normal !important',
                              resize: 'vertical',
                            },
                          }}
                        />
                      ) : (
                        <RichTextEditor
                          key={`editor-${page?.id}-${
                            page?.content?.length || 0
                          }`}
                          value={values.content || ''}
                          onChange={(html) => {
                            setFieldValue('content', html);
                            updatePreview(html);
                          }}
                        />
                      )}
                    </StickyCard>
                  </Grid>

                  {/* Preview Section */}
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <StickyCard title={t('preview')}>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1,
                          mb: 2,
                          height: '600px',
                          overflow: 'auto',
                        }}
                      >
                        <iframe
                          key={`iframe-${previewHtml.length || 0}`}
                          ref={previewIframeRef}
                          style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                          }}
                          title={values.name}
                          srcDoc={previewHtml}
                        />
                      </Box>
                    </StickyCard>
                  </Grid>
                </Grid>
              </>
            )}
          </Form>
        )}
      </Formik>
    </Box>
  );
};
